import { faDownload, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import { orderBy } from 'lodash';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import type { DocumentCategory } from 'src/apis/monolith/types';
import type { DocumentsRequest } from 'src/apis/monolith/use-documents';
import type { ShipperPlatformIdsRequest } from 'src/apis/monolith/use-shipper-platform-ids';
import { Button } from 'src/components/buttons-and-actions/button';
import { FormatDate } from 'src/components/data-display/format-date';
import { Table } from 'src/components/data-display/smart-table';
import { SpecificSortHeader, Td } from 'src/components/data-display/table';
import { Form } from 'src/components/form/form';
import { Input } from 'src/components/form/input';
import { MonolithDownload } from 'src/components/monolith-download';
import { Tooltip } from 'src/components/overlay/tooltip';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';
import { useTitle } from 'src/hooks/use-title';
import { usePageParams } from 'src/pages/shippers/details/documents/from-shipper/use-page-params';

const documentLabels: Record<DocumentCategory, string> = {
  SOLVENCY: 'Solvency',
  MANDATE: 'Mandate Check',
  MISC: 'Others',
  FOR_TSOS: 'Documents for TSO',
  FOR_CUSTOMERS: 'Documents for Customers',
  PUBLIC_DOWNLOAD: 'General Information',
  COUNTRY: 'Country Specific Information',
  LEGAL: 'Legal Information',
  NEWS: 'News',
  DYNAMIC_MANDATE_CHECK: 'Dynamic Mandate Check',
  PLATFORM_TRADE_CONDITIONS: 'EFET Gas Capacity Contract',
  SHIPPER_FORMS: 'Form - Change of Company Information',
  SHIPPER_USER_FORMS: 'Form - Change of User Information',
};

const schema = z.object({
  search: z.string().nullable(),
});

type FormValues = z.input<typeof schema>;

type Props = {
  shipperName: string;
  documents: DocumentsRequest;
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
};

const FromShipperPage: FC<Props> = ({
  shipperName,
  documents,
  shipperPlatformIds,
}) => {
  const shipperRefId = shipperPlatformIds?.response.data.referenceId.toString();
  useTitle(`${shipperName} - Documents`);

  if (!documents.response) return <PageSpinner />;

  return (
    <Form
      schema={schema}
      initialValues={{ search: null }}
      onSubmit={() => {}}
      constraintViolation={null}
      cancelOrResetButton={null}
      submitButton={null}
    >
      {({ setFieldValue }) => (
        <>
          <Input
            name="search"
            label="Find Document"
            placeholder="Filter by Name"
            leftAddon={<FontAwesomeIcon icon={faSearch} aria-hidden="true" />}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setFieldValue('search', null);
            }}
            stacked
          />

          <FilteredDocuments
            shipperRefId={shipperRefId}
            documents={documents}
          />
        </>
      )}
    </Form>
  );
};

const FilteredDocuments: FC<{
  shipperRefId: string | undefined;
  documents: Successful<DocumentsRequest>;
}> = ({ shipperRefId, documents }) => {
  const { values, resetForm } = useFormikContext<FormValues>();
  const pageParams = usePageParams();

  const search = useClientSearch(documents.response.data, (document, search) =>
    document.fileName.toLowerCase().match(search)
  );

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => search.setSearchValue(values.search ?? ''), [values.search]);

  const sortedCollection = useMemo(
    () =>
      orderBy(
        search.options,
        pageParams.value.sortBy,
        pageParams.value.sortDirection
      ),
    [search.options, pageParams.value.sortBy, pageParams.value.sortDirection]
  );

  return (
    <Table
      data={sortedCollection}
      setId={(data) => data.documentId}
      cols={[
        {
          key: 'changedAt',
          head: (
            <SpecificSortHeader
              label="Date"
              column="changedAt"
              params={pageParams}
              sortParam="sortBy"
              directionParam="sortDirection"
            />
          ),
          body: (data, index) => (
            <Td data-testid={`changedAt-${index}`}>
              <FormatDate value={data.changedAt} type="date-time" />
            </Td>
          ),
        },
        {
          key: 'fileName',
          head: (
            <SpecificSortHeader
              label="Name"
              column="fileName"
              params={pageParams}
              sortParam="sortBy"
              directionParam="sortDirection"
            />
          ),
          body: (data, index) => (
            <Td data-testid={`fileName-${index}`}>
              <Tooltip
                content="Content available for download has been provided by the operator. PRISMA assumes no responsibility for any issues or security risks that may arise from using these files."
                noTabIndex
              >
                {(targetProps) => (
                  <>
                    <MonolithDownload
                      href={`${PRISMA_CONFIG.monolithApiUrl}/shippers/${shipperRefId}/document/${data.documentId}`}
                      mode="link"
                      {...targetProps}
                    >
                      {data.fileName}
                    </MonolithDownload>{' '}
                    <FontAwesomeIcon icon={faDownload} aria-hidden />
                  </>
                )}
              </Tooltip>
            </Td>
          ),
        },
        {
          key: 'category',
          width: 2,
          head: (
            <SpecificSortHeader
              label="Type"
              column="category"
              params={pageParams}
              sortParam="sortBy"
              directionParam="sortDirection"
            />
          ),
          body: (data, index) => (
            <Td data-testid={`category-${index}`}>
              {documentLabels[data.category]}
            </Td>
          ),
        },
      ]}
      empty={
        search.searchValue
          ? {
              title: 'No Matching Results',
              description:
                'No documents were found matching your search criteria. Try changing or resetting the filters.',
              actions: (
                <Button
                  data-testid="empty-action-button"
                  onClick={() => resetForm()}
                >
                  Reset Filters
                </Button>
              ),
            }
          : {
              title: 'No Documents',
              description: 'No documents were found for this shipper.',
            }
      }
    />
  );
};

export default FromShipperPage;
